import { Calendar, LogOut, Mail, MapPin, Phone, User, Edit2, X, Save, Camera, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminAuth } from "../../../context/AdminAuthContext";
import { formatDate } from "../../../utils/helpers";
import { showError, showSuccess } from "../../../utils/toastUtils";
import axios from "axios";
import uploadFileToCloudinary from './../../../utils/uploadFileToCloudinary';
import logo from "../../../Assets/Images/FooterImages/Logo1.webp";


const InfoRow = ({ icon: Icon, label, value }) => {
  if (!value) return null;

  return (
    <div className="flex items-start gap-2 md:gap-3 p-2 md:p-3 rounded-lg hover:bg-gray-50 transition">
      <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-blue-50 flex-shrink-0">
        <Icon size={16} className="md:w-[18px] md:h-[18px] text-blue-600" />
      </div>

      <div className="flex flex-col flex-1 min-w-0">
        <span className="text-[10px] md:text-xs text-gray-500 font-medium">{label}</span>
        <span className="text-xs md:text-sm font-semibold text-gray-800 break-words mt-0.5">
          {value}
        </span>
      </div>
    </div>
  );
};

const AdminProfile = () => {
  const { admin, logoutAdmin, setAdmin, isManager, isAdmin } = useAdminAuth();
  const navigate = useNavigate();

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewImage, setPreviewImage] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    designation: "",
    profilePicture: "",
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (admin) {
      setFormData({
        name: admin.name || "",
        email: admin.email || "",
        phoneNumber: admin.phoneNumber || "",
        designation: admin.designation || "",
        profilePicture: admin.profilePicture || "",
      });
    }
  }, [admin]);

  const handleLogout = async () => {
    const isLoggedOut = await logoutAdmin();
    if (isLoggedOut) navigate("/admin-login");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      showError("Please upload an image file");
      return;
    }

    try {
      setUploadingPhoto(true);
      setUploadProgress(0);

      const { uploadPromise } = uploadFileToCloudinary(file, (progress) => {
        setUploadProgress(progress);
      });

      const result = await uploadPromise;

      if (result?.url) {
        setFormData((prev) => ({ ...prev, profilePicture: result.url }));
        showSuccess("Photo uploaded successfully");
      }
    } catch (error) {
      showError("Failed to upload photo");
      console.error(error);
    } finally {
      setUploadingPhoto(false);
      setUploadProgress(0);
    }
  };

  const handleSave = async () => {
    // Validation
    if (!formData.name?.trim()) {
      showError("Name is required");
      return;
    }

    if (!formData.email?.trim()) {
      showError("Email is required");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      showError("Please enter a valid email");
      return;
    }

    if (formData.phoneNumber && !/^\d{10}$/.test(formData.phoneNumber)) {
      showError("Phone number must be 10 digits");
      return;
    }

    try {
      setIsSaving(true);

      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/admins/update/${admin.id}`,
        formData
      );

      if (response.data.success) {
        localStorage.setItem("adminData", JSON.stringify(response.data.data));
        setAdmin(response.data.data);
        console.log(response.data.data);
        showSuccess("Profile updated successfully");
        setIsEditing(false);
      }
    } catch (error) {
      const message = error.response?.data?.message || "Failed to update profile";
      showError(message);
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset form to original values
    setFormData({
      name: admin.name || "",
      email: admin.email || "",
      phoneNumber: admin.phoneNumber || "",
      designation: admin.designation || "",
      profilePicture: admin.profilePicture || "",
    });
    setIsEditing(false);
  };

  if (!admin) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="animate-spin text-blue-600" size={40} />
      </div>
    );
  }

  const roleLabel = isAdmin ? "Super Admin" : isManager ? "Branch Manager" : "Staff";
  const displayPhoto = isEditing ? formData.profilePicture : admin.profilePicture;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30 pb-20 md:pb-6">
      {/* Header - Sticky with better mobile spacing */}
      <div className="sticky top-0 z-10 bg-gradient-to-r from-primaryColor/10 to-accentTextColor/10 backdrop-blur-md border-b shadow-sm">
        <div className="px-3 sm:px-4 md:px-6 py-3 md:py-4">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0 flex-1">
              <h1 className="text-base md:text-xl font-bold text-primaryTextColor">
                My Profile
              </h1>
              <p className="text-xs md:text-sm text-gray-600 mt-0.5">
                View and manage your account details
              </p>
            </div>

            {!isEditing ? (
              <button
                onClick={() => setIsEditing(true)}
                className="flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-2 bg-primaryColor text-white rounded-lg hover:bg-primaryColor/90 active:scale-95 transition shadow-sm text-sm"
              >
                <Edit2 size={14} className="md:w-4 md:h-4" />
                <span className="hidden xs:inline">Edit</span>
                <span className="hidden sm:inline">Edit</span>
              </button>
            ) : (
              <div className="flex gap-1.5 md:gap-2">
                <button
                  onClick={handleCancel}
                  disabled={isSaving}
                  className="flex items-center gap-1.5 px-2.5 md:px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 active:scale-95 transition disabled:opacity-50 text-sm"
                >
                  <X size={14} className="md:w-4 md:h-4" />
                  <span className="hidden sm:inline">Cancel</span>
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="flex items-center gap-1.5 px-2.5 md:px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 active:scale-95 transition disabled:opacity-50 shadow-sm text-sm"
                >
                  {isSaving ? (
                    <>
                      <Loader2 size={14} className="md:w-4 md:h-4 animate-spin" />
                      <span className="hidden sm:inline">Saving...</span>
                    </>
                  ) : (
                    <>
                      <Save size={14} className="md:w-4 md:h-4" />
                      <span className="hidden sm:inline">Save</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="px-3 sm:px-4 md:px-6 py-4 md:py-6 space-y-4 md:space-y-6">
        {/* Profile Card with Photo - Mobile optimized */}
        <div className="bg-white rounded-xl md:rounded-2xl border shadow-sm overflow-hidden">
          {/* Banner - Shorter on mobile */}
          <div style={{ backgroundImage: `url(${logo})` }} className="h-24 md:h-32 opacity-20 bg-contain bg-no-repeat bg-center"></div>

          <div className="px-4 md:px-6 pb-4 md:pb-6">
            {/* Profile Photo - Smaller on mobile */}
            <div className="relative -mt-12 md:-mt-16 mb-3 md:mb-4">
              <div className="relative inline-block">
                {displayPhoto ? (
                  <img
                    src={displayPhoto}
                    onClick={() => setPreviewImage(displayPhoto)}
                    alt={admin.name}
                    className="w-24 h-24 md:w-32 cursor-pointer md:h-32 rounded-full border-4 border-white shadow-lg object-cover bg-gray-100"
                  />
                ) : (
                  <div className="w-24 h-24 md:w-32 md:h-32 rounded-full border-4 border-white shadow-lg bg-gradient-to-br from-primaryColor via-brandAccentColor to-primaryColor text-white flex items-center justify-center text-3xl md:text-4xl font-bold">
                    {admin.name?.charAt(0)?.toUpperCase()}
                  </div>
                )}

                {isEditing && (
                  <label className="absolute bottom-0 right-0 md:bottom-1 md:right-1 w-8 h-8 md:w-10 md:h-10 bg-blue-600 text-white rounded-full flex items-center justify-center cursor-pointer hover:bg-blue-700 active:scale-95 transition shadow-lg">
                    {uploadingPhoto ? (
                      <Loader2 size={16} className="md:w-[18px] md:h-[18px] animate-spin" />
                    ) : (
                      <Camera size={16} className="md:w-[18px] md:h-[18px]" />
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      className="hidden"
                      disabled={uploadingPhoto}
                    />
                  </label>
                )}

                {uploadingPhoto && (
                  <div className="absolute -bottom-6 left-0 right-0 text-center">
                    <div className="text-xs text-blue-600 font-medium">
                      {uploadProgress}%
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Name & Role - Better mobile layout */}
            <div className="mt-2">
              {isEditing ? (
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="text-xl md:text-2xl font-bold text-gray-900 border-b-2 border-blue-600 focus:outline-none bg-transparent w-full"
                  placeholder="Your Name"
                />
              ) : (
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 break-words">
                  {admin.name}
                </h2>
              )}

              <div className="flex flex-wrap items-center gap-1.5 md:gap-2 mt-2">
                <span className="px-2 md:px-3 py-0.5 md:py-1 rounded-full text-xs md:text-sm font-semibold bg-blue-100 text-blue-700">
                  {roleLabel}
                </span>
                {!isAdmin && <span className="px-2 md:px-3 py-0.5 md:py-1 rounded-full text-xs md:text-sm font-semibold bg-cyan-100 text-cyan-700 max-w-[180px] md:max-w-none truncate">
                  {admin.assignedBranch?.branchName || "N/A"}
                </span>}
                {(isEditing ? formData.designation : admin.designation) && (
                  <span className="text-xs md:text-sm text-gray-600 hidden sm:inline">
                    • {isEditing ? formData.designation : admin.designation}
                  </span>
                )}
              </div>

              {/* Designation on mobile (if not editing) */}
              {!isEditing && admin.designation && (
                <p className="text-xs text-gray-600 mt-1 sm:hidden">
                  {admin.designation}
                </p>
              )}

              <p className="text-xs md:text-sm text-gray-500 mt-2 flex items-center gap-1">
                <Calendar size={12} className="md:w-3.5 md:h-3.5" />
                <span className="hidden sm:inline">Member since {formatDate(admin.createdAt)}</span>
                <span className="sm:hidden">Since {new Date(admin.createdAt).toLocaleDateString("en-IN", { month: "short", year: "numeric" })}</span>
              </p>
            </div>
          </div>
        </div>

        {/* Information Cards Grid - Better mobile stacking */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          {/* Contact Information */}
          <div className="bg-white rounded-xl md:rounded-2xl border shadow-sm p-4 md:p-6">
            <h3 className="text-base md:text-lg font-bold text-gray-900 mb-3 md:mb-4 flex items-center gap-2">
              <Mail size={18} className="md:w-5 md:h-5" />
              Contact Information
            </h3>

            <div className="space-y-1">
              {isEditing ? (
                <>
                  {/* Email Input */}
                  <div className="p-2.5 md:p-3 rounded-lg border border-gray-200 hover:border-blue-300 transition">
                    <label className="text-[10px] md:text-xs text-gray-500 font-medium block mb-1">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full text-xs md:text-sm font-semibold text-gray-800 focus:outline-none bg-transparent"
                      placeholder="your.email@example.com"
                    />
                  </div>

                  {/* Phone Input */}
                  <div className="p-2.5 md:p-3 rounded-lg border border-gray-200 hover:border-blue-300 transition">
                    <label className="text-[10px] md:text-xs text-gray-500 font-medium block mb-1">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      maxLength={10}
                      className="w-full text-xs md:text-sm font-semibold text-gray-800 focus:outline-none bg-transparent"
                      placeholder="10-digit mobile number"
                    />
                  </div>


                </>
              ) : (
                <>
                  <InfoRow icon={Mail} label="Email Address" value={admin.email} />
                  <InfoRow icon={Phone} label="Phone Number" value={admin.phoneNumber} />
                  <InfoRow icon={User} label="Designation" value={admin.designation} />
                </>
              )}
            </div>
          </div>

          {/* Additional Details */}
          <div className="bg-white rounded-xl md:rounded-2xl border shadow-sm p-4 md:p-6">
            <h3 className="text-base md:text-lg font-bold text-gray-900 mb-3 md:mb-4 flex items-center gap-2">
              <MapPin size={18} className="md:w-5 md:h-5" />
              Additional Details
            </h3>

            <div className="space-y-1">
              <InfoRow icon={MapPin} label="Address" value={admin.address} />
              <InfoRow
                icon={Calendar}
                label="Account Created"
                value={formatDate(admin.createdAt)}
              />
              <InfoRow icon={User} label="User Type" value={roleLabel} />
            </div>
          </div>
        </div>

        {/* Logout Button - Mobile optimized */}
        <div className="bg-white rounded-xl md:rounded-2xl border shadow-sm p-4 md:p-6">
          <h3 className="text-base md:text-lg font-bold text-gray-900 mb-3 md:mb-4">
            Account Actions
          </h3>

          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 md:gap-3 px-4 py-2.5 md:py-3 rounded-xl border-2 border-red-200 text-red-600 hover:bg-red-50 active:scale-[0.98] transition font-medium text-sm md:text-base"
          >
            <LogOut size={18} className="md:w-5 md:h-5" />
            <span>Logout from Account</span>
          </button>
        </div>
      </div>

      {previewImage && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setPreviewImage(null)}
        >
          <img
            src={previewImage}
            className="max-h-[80vh] max-w-full object-contain rounded-xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

    </div>
  );
};

export default AdminProfile;